import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { StorageContext } from './contexts/StorageContext';
import { AuthContext } from './contexts/AuthContext';
import StorageManager from './services/StorageManager';
import AuthService from './services/AuthService';
import HomePage from './pages/HomePage';
import ProjectEditor from './pages/ProjectEditor';
import ProjectSettings from './pages/ProjectSettings';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ProfilePage from './pages/ProfilePage';
import AboutPage from './pages/AboutPage';
import PremiumPage from './pages/PremiumPage';
import Header from './components/Header';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import StorageWarning from './components/StorageWarning';
import { isLocalStorageAvailable, isIndexedDBAvailable } from './utils/configUtils';
import './App.css';

/**
 * Main application component
 */
function App() {
  const [storage] = useState(new StorageManager());
  const [auth] = useState(new AuthService());
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [storageAvailable, setStorageAvailable] = useState(true);
  
  // Initialize storage and check authentication
  useEffect(() => {
    const init = async () => {
      try {
        // Check if storage is available
        const localStorageAvailable = isLocalStorageAvailable();
        const indexedDBAvailable = await isIndexedDBAvailable();
        
        if (!localStorageAvailable || !indexedDBAvailable) {
          setStorageAvailable(false);
          setInitialized(true);
          return;
        }
        
        // Initialize storage
        await storage.initDatabase();
        
        // Check authentication status
        const authStatus = auth.isAuthenticated();
        setIsAuthenticated(authStatus);
        
        if (authStatus) {
          setUser(auth.getCurrentUser());
        }
        
        setInitialized(true);
      } catch (error) {
        console.error('Initialization error:', error);
        setStorageAvailable(false);
        setInitialized(true);
      }
    };
    
    init();
  }, [storage, auth]);
  
  // Handle login/logout
  const handleLogin = async (credentials) => {
    try {
      const user = await auth.login(credentials);
      setIsAuthenticated(true);
      setUser(user);
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };
  
  const handleLogout = () => {
    auth.logout();
    setIsAuthenticated(false);
    setUser(null);
  };
  
  // Display loading state
  if (!initialized) {
    return (
      <div className="loading-app">
        <div className="loading-content">
          <div className="app-logo">AI Code Implementer</div>
          <div className="loading-spinner"></div>
          <div className="loading-message">Initializing application...</div>
        </div>
      </div>
    );
  }
  
  // Display storage warning if storage is not available
  if (!storageAvailable) {
    return <StorageWarning />;
  }
  
  return (
    <ErrorBoundary>
      <StorageContext.Provider value={{ storage }}>
        <AuthContext.Provider value={{ 
          isAuthenticated, 
          user, 
          login: handleLogin, 
          logout: handleLogout,
          isPremium: isAuthenticated && user?.subscription === 'premium'
        }}>
          <BrowserRouter>
            <div className="app-container">
              <Header />
              
              <main className="app-content">
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/project/:projectId/*" element={<ProjectEditor />} />
                  <Route path="/project/:projectId/settings" element={<ProjectSettings />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  {/* Make profile page accessible without login requirement */}
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/premium" element={<PremiumPage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </main>
              
              <Footer showAds={!isAuthenticated || user?.subscription !== 'premium'} />
            </div>
          </BrowserRouter>
        </AuthContext.Provider>
      </StorageContext.Provider>
    </ErrorBoundary>
  );
}

export default App;